import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ErrorBoundary from './ErrorBoundary';
import {
  LayoutDashboard, Users, Calendar, ClipboardList,
  FileText, Activity, Apple, Droplets, Settings, LogOut, Menu, X, Stethoscope, BarChart2
} from 'lucide-react';
import { useState } from 'react';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Tableau de Bord' },
  { path: '/patients', icon: Users, label: 'Patients' },
  { path: '/appointments', icon: Calendar, label: 'Rendez-vous' },
  { path: '/consultations/new', icon: ClipboardList, label: 'Consultation' },
  { path: '/prescriptions', icon: FileText, label: 'Ordonnances' },
  { path: '/stats', icon: BarChart2, label: 'Statistiques' },
  { path: '/diabetes', icon: Activity, label: 'Diabète' },
  { path: '/nutrition', icon: Apple, label: 'Nutrition' },
  { path: '/renal', icon: Droplets, label: 'Néphrologie' },
  { path: '/settings', icon: Settings, label: 'Paramètres' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-6 py-5 border-b border-stone-200">
        <div className="w-9 h-9 bg-emerald-600 rounded-lg flex items-center justify-center shadow-sm">
          <Stethoscope className="text-white" size={20} />
        </div>
        <div>
          <p className="font-heading font-700 text-stone-900 text-base leading-tight">MediConsult</p>
          <p className="text-xs text-stone-500">Maroc</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {navItems.map(({ path, icon: Icon, label }) => (
          <NavLink
            key={path}
            to={path}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-150 ${
                isActive
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
              }`
            }
          >
            <Icon size={18} />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* User info */}
      <div className="px-4 py-4 border-t border-stone-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
            <span className="text-emerald-700 font-semibold text-sm">
              {user?.nom?.charAt(0)?.toUpperCase() || 'D'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-stone-800 truncate">{user?.nom}</p>
            <p className="text-xs text-stone-500 truncate">Médecin Généraliste</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
        >
          <LogOut size={16} />
          Déconnexion
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-stone-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        data-testid="sidebar"
        className="hidden lg:flex flex-col w-60 bg-white border-r border-stone-200 flex-shrink-0 no-print"
      >
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <aside className="relative z-50 flex flex-col w-64 bg-white border-r border-stone-200 h-full">
            <button className="absolute top-4 right-4 p-1 text-stone-500" onClick={() => setMobileOpen(false)}>
              <X size={20} />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-stone-200 h-14 flex items-center px-4 lg:px-6 flex-shrink-0 z-10 no-print">
          <button
            className="lg:hidden mr-3 p-1 text-stone-500 hover:text-stone-700"
            onClick={() => setMobileOpen(true)}
          >
            <Menu size={22} />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-sm text-stone-500">
            <span className="font-medium text-stone-700">{user?.nom}</span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
